import * as Toast from '@radix-ui/react-toast';
import {
  FUN_KEY,
  NotifyHelper,
  TOAST_KEY,
} from 'classes/helpers/notify.helper';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonToast } from 'components/common/toast';
import { lightFormat } from 'date-fns';
import useLatest from 'hooks/useLatestRef';
import { INotification, INotificationButton } from 'interfaces/i-notification';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { IUserErrorMsg } from 'lib_ts/interfaces/machine-msg/i-error';
import { FC, useEffect, useState } from 'react';
import Confetti from 'react-confetti';
import { useAuthStore } from 'stores/use-auth-store';
import { useCookiesStore } from 'stores/use-cookies-store';
import useInboxStore from 'stores/use-inbox-store';
import useMiscStore from 'stores/use-misc-store';
import { v4 } from 'uuid';
import './index.scss';

const RESTART_LABEL = 'common.restart';

const DAY_COLOR_DICT: { [mmdd: string]: string[] | undefined } = {
  '0101': ['white', 'blue'],
  '0214': ['white', 'pink', 'red'],
  '0317': ['white', 'green', 'orange'],
  '0701': ['red', 'white'],
  '0704': ['red', 'blue', 'white'],
  '1031': ['orange', 'green', 'purple', 'white'],
  '1225': ['red', 'green', 'white'],
};

const getDayColors = () => {
  const today = lightFormat(new Date(), 'MMdd');
  return DAY_COLOR_DICT[today];
};

interface IProps {
  onRestartOS?: () => void;
}

export const NotificationListener: FC<IProps> = (props) => {
  const current = useAuthStore((s) => s.current);

  const app = useCookiesStore((s) => s.app);

  const [notifications, setNotifications] = useState<INotification[]>([]);
  const [fun, setFun] = useState(false);
  const [colors] = useState(getDayColors() ?? ['green', 'yellow']);

  // clear the inbox whenever session changes
  useEffect(() => {
    useInboxStore.getState().removeAll();
  }, [current.session]);

  // handle user errors
  useEffect(() => {
    const callback = (event: CustomEvent) => {
      try {
        const data: IUserErrorMsg = event.detail;

        // SessionEventsService.createOne({
        //   category: 'machine',
        //   tags: 'errormsg',
        //   data: data,
        // });

        const type = data.type;

        if (!type.user_message || typeof type.user_message !== 'string') {
          console.error({
            event: 'Invalid machine error-types data',
            type: type,
          });

          NotifyHelper.error({
            message_md:
              'Received machine error with invalid data. Check console.',
            delay_ms: 0,
            inbox: true,
          });
          return;
        }

        NotifyHelper.userError(type);
      } catch (e) {
        console.error('NotifyListener caught an error in handleErrorMsg', e);
      }
    };

    WebSocketHelper.on(WsMsgType.Misc_Error, callback);
    return () => {
      WebSocketHelper.remove(WsMsgType.Misc_Error, callback);
    };
  }, []);

  // handle server announcements
  useEffect(() => {
    const callback = (event: CustomEvent) =>
      NotifyHelper.announce(event.detail);

    WebSocketHelper.on(WsMsgType.S2U_Announcement, callback);

    return () => {
      WebSocketHelper.remove(WsMsgType.S2U_Announcement, callback);
    };
  });

  const latestRestartOS = useLatest(props.onRestartOS);

  // handle toasts
  useEffect(() => {
    const callback = (event: any) => {
      try {
        const data: INotification = {
          ...event.detail,
          id: v4(),
        };

        if (data.debug && !app.enable_debug_toasts) {
          // filter out debug toasts if disabled in cookies
          console.debug({
            ...data,
            event: 'suppressed debug toast',
          });
          return;
        }

        if (data.show_restart && props) {
          const restartBtn: INotificationButton = {
            label: RESTART_LABEL,
            kind: 'restart',
            dismissAfterClick: true,
            onClick: () => latestRestartOS.current?.(),
          };

          if (!data.buttons) {
            data.buttons = [restartBtn];
          } else if (!data.buttons.some((b) => b.kind === 'restart')) {
            data.buttons.push(restartBtn);
          }
        }

        if (data.inbox) {
          useInboxStore.getState().add(data);
        }

        setNotifications((prev) => [...prev.filter((n) => !n.closed), data]);
      } catch (e) {
        console.error('NotifyListener caught an error in handleToast', e);
      }
    };

    // * order matters: add the listener *before* marking ready to toast
    document.addEventListener(TOAST_KEY, callback);
    useMiscStore.setState({ readyToToast: true });

    return () => {
      useMiscStore.setState({ readyToToast: false });
      document.removeEventListener(TOAST_KEY, callback);
    };
  }, [app.enable_debug_toasts]);

  // handle fun
  useEffect(() => {
    const callback = () => {
      setFun(true);
    };

    document.addEventListener(FUN_KEY, callback);

    return () => document.removeEventListener(FUN_KEY, callback);
  }, []);

  // prevent repeated fun triggers for the same holiday
  useEffect(() => {
    const today = lightFormat(new Date(), 'MMdd');

    if (getDayColors() && localStorage.getItem('last-fun') !== today) {
      localStorage.setItem('last-fun', today);
      NotifyHelper.haveFun();
    }
  }, []);

  return (
    <ErrorBoundary identifier="NotifyListener">
      {/* provide it here to ensure it exists where machine context is defined */}
      <Toast.Provider swipeDirection="right" duration={60_000}>
        {notifications
          .filter((n) => !n.closed)
          .map((n) => (
            <CommonToast key={n.id} config={n} />
          ))}
        <Toast.Viewport className="ToastViewport top-right" />
      </Toast.Provider>

      {fun && (
        <Confetti
          colors={colors}
          drawShape={(ctx: CanvasRenderingContext2D) => {
            ctx.beginPath();
            ctx.fillStyle = ArrayHelper.getRandomMember(colors);
            ctx.fillRect(0, 0, 10, 10);
            ctx.closePath();
          }}
          recycle={false}
          onConfettiComplete={() => setFun(false)}
          style={{
            // draw above dialogs
            zIndex: 2000,
          }}
        />
      )}
    </ErrorBoundary>
  );
};
